import React from 'react';

// The subject, text and attachment of an email about to go, on every send screen. What these hold
// is what the server sends, word for word (D180, BUSINESS_RULES#outgoing-email-as-shown).

const LABEL_STYLE: React.CSSProperties = { fontFamily: 'Public Sans', fontWeight: 500, fontSize: '14px', lineHeight: '22px' };
const INPUT_STYLE: React.CSSProperties = { fontFamily: 'Public Sans', fontWeight: 400, fontSize: '16px', lineHeight: '24px' };

export interface EmailDraftFieldsProps {
  subject: string;
  onSubjectChange: (value: string) => void;
  body: string;
  onBodyChange: (value: string) => void;
  /** The file that goes with it, in words, e.g. "Quotation QUO-00033 (Excel)". */
  attachment?: string | null;
  subjectError?: string;
  bodyError?: string;
  disabled?: boolean;
  /** Goes beside the Email text label as "Restore default" when given. */
  onRestoreDefault?: () => void;
  /** Element ids start with this; give each its own when several sit on one screen. */
  idPrefix?: string;
}

const EmailDraftFields: React.FC<EmailDraftFieldsProps> = ({
  subject,
  onSubjectChange,
  body,
  onBodyChange,
  attachment,
  subjectError,
  bodyError,
  disabled = false,
  onRestoreDefault,
  idPrefix = 'email-draft',
}) => (
  <>
    <div className="mb-4">
      <label className="block mb-2 text-slate-900" style={LABEL_STYLE} htmlFor={`${idPrefix}-subject`}>
        Subject <span className="text-red-500">*</span>
      </label>
      <input
        id={`${idPrefix}-subject`}
        aria-label="Subject"
        type="text"
        value={subject}
        disabled={disabled}
        onChange={(e) => onSubjectChange(e.target.value)}
        placeholder="Enter Subject"
        className="w-full px-3 py-2 border border-[#D7DBD9] rounded-md bg-white placeholder-[#999999] text-slate-900"
        style={INPUT_STYLE}
      />
      {subjectError && (
        <p role="alert" className="mt-1 text-xs text-red-600" style={{ fontFamily: 'Public Sans' }}>
          {subjectError}
        </p>
      )}
    </div>
    <div className="mb-4">
      <div className="flex items-center justify-between mb-2">
        <label className="text-slate-900" style={LABEL_STYLE} htmlFor={`${idPrefix}-body`}>
          Email text <span className="text-red-500">*</span>
        </label>
        {onRestoreDefault && (
          <button type="button" onClick={onRestoreDefault} disabled={disabled} className="text-xs font-semibold text-primary-500 hover:underline disabled:opacity-50">
            Restore default
          </button>
        )}
      </div>
      <textarea
        id={`${idPrefix}-body`}
        aria-label="Email text"
        value={body}
        disabled={disabled}
        onChange={(e) => onBodyChange(e.target.value)}
        rows={10}
        className="w-full px-3 py-2 border border-[#D7DBD9] rounded-md bg-white text-slate-900 resize-y"
        style={INPUT_STYLE}
      />
      <p className="mt-1 text-xs text-slate-500" style={{ fontFamily: 'Public Sans' }}>
        This is exactly what will be sent. Nothing is added to it.
      </p>
      {bodyError && (
        <p role="alert" className="mt-1 text-xs text-red-600" style={{ fontFamily: 'Public Sans' }}>
          {bodyError}
        </p>
      )}
    </div>
    {attachment && (
      <p className="mb-4 text-sm text-slate-700" style={{ fontFamily: 'Public Sans' }}>
        <span className="font-medium">Attached:</span> {attachment}
      </p>
    )}
  </>
);

export default EmailDraftFields;
